import axios from 'axios'
import { useEffect, useRef, useState } from 'react'
import moment from 'moment'
import ReactHtmlParser from 'react-html-parser'
import { Modal, ModalBody, ModalHeader } from 'reactstrap'
import { getAwsImage } from '../../utils/awsPaths'

const plainText = (html) => {
  const doc = new DOMParser().parseFromString(html || '', 'text/html')
  return (doc.body.textContent || '').replace(/\s+/g, ' ').trim()
}

export default function FeedSearch() {
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)
  const [retry, setRetry] = useState(0)
  const [results, setResults] = useState([])
  const [total, setTotal] = useState(0)
  const [hasNext, setHasNext] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  const [selected, setSelected] = useState(null)
  const root = useRef(null)
  const input = useRef(null)
  const term = query.trim()

  useEffect(() => {
    const outside = (event) => {
      if (root.current && !root.current.contains(event.target)) setOpen(false)
    }
    document.addEventListener('mousedown', outside)
    return () => document.removeEventListener('mousedown', outside)
  }, [])

  useEffect(() => {
    if (!term) return
    let current = true
    const cancel = axios.CancelToken.source()
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.get(`feeds/${page}`, { params: { q: term }, cancelToken: cancel.token })
        if (!data.success) throw new Error('Search failed')
        if (current) {
          setResults((previous) => page === 1 ? data.data.docs : [...previous, ...data.data.docs])
          setTotal(data.data.totalDocs)
          setHasNext(data.data.hasNextPage)
        }
      } catch (err) {
        if (current && !axios.isCancel(err)) setError(true)
      } finally {
        if (current) setLoading(false)
      }
    }, 300)
    return () => { current = false; clearTimeout(timer); cancel.cancel() }
  }, [query, page, retry])

  const changeQuery = (value) => {
    setQuery(value)
    setPage(1)
    setResults([])
    setHasNext(false)
    setTotal(0)
    setActive(-1)
    setError(false)
    setLoading(!!value.trim())
    setOpen(!!value.trim())
  }
  const choose = (feed) => { setSelected(feed); setOpen(false) }
  const keyDown = (event) => {
    if (event.key === 'Escape') { setOpen(false); return }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      setOpen(!!term)
      const next = event.key === 'ArrowDown' ? Math.min(active + 1, results.length - 1) : Math.max(active - 1, 0)
      setActive(next)
      document.getElementById(`feed-search-result-${next}`)?.scrollIntoView({ block: 'nearest' })
    }
    if (event.key === 'Enter' && open && results[active]) { event.preventDefault(); choose(results[active]) }
  }

  return (
    <>
      <div className='feed-search' ref={root}>
        <div className='feed-search-input'>
          <em className='icon-magnifier' aria-hidden='true' />
          <input ref={input} role='combobox' aria-label='Kërko postime' aria-expanded={open && !!term} aria-controls='feed-search-results' aria-autocomplete='list'
            aria-activedescendant={open && active >= 0 ? `feed-search-result-${active}` : undefined}
            placeholder='Kërko postime…' value={query} onChange={(e) => changeQuery(e.target.value)} onFocus={() => setOpen(!!term)} onKeyDown={keyDown} />
          {query && <button type='button' aria-label='Pastro kërkimin' onClick={() => { changeQuery(''); input.current.focus() }}>×</button>}
        </div>
        {open && term && (
          <div className='feed-search-dropdown'>
            <div className='feed-search-heading' role='status'>
              {loading ? 'Duke kërkuar…' : error ? 'Kërkimi dështoi. Provoni përsëri.' : `Postime (${total})`}
            </div>
            <div id='feed-search-results' role='listbox' aria-label='Rezultatet e kërkimit' aria-busy={loading}>
              {results.map((feed, index) => (
                <button type='button' role='option' aria-selected={active === index} id={`feed-search-result-${index}`} key={feed._id}
                  className={`feed-search-result ${active === index ? 'is-active' : ''}`} onMouseEnter={() => setActive(index)} onClick={() => choose(feed)}>
                  <span className='feed-search-icon'><em className='icon-doc' aria-hidden='true' /></span>
                  <span className='feed-search-copy'>
                    <strong>{feed.title}</strong>
                    <span className='feed-search-excerpt'>{plainText(feed.description).slice(0, 160)}</span>
                    <span className='feed-search-meta'>{[feed.created_by?.first_name, feed.created_by?.last_name].filter(Boolean).join(' ')} · {moment(feed.posted_date).format('DD MMM YYYY')}</span>
                  </span>
                  <em className='icon-arrow-right feed-search-arrow' aria-hidden='true' />
                </button>
              ))}
            </div>
            {!loading && !error && !results.length && <p className='feed-search-empty'>Nuk u gjet asnjë postim.</p>}
            {hasNext && !error && <button type='button' className='feed-search-more' disabled={loading} onClick={() => { setLoading(true); setPage(page + 1) }}>Shfaq më shumë</button>}
            {error && <button type='button' className='feed-search-more' onClick={() => { setError(false); setLoading(true); setRetry(retry + 1) }}>Provo përsëri</button>}
            <div className='feed-search-footer'>↑ ↓ Navigo <span>↵ Hap postimin</span><span>Esc Mbyll</span></div>
          </div>
        )}
      </div>
      <Modal isOpen={!!selected} toggle={() => setSelected(null)} size='lg' onClosed={() => input.current?.focus()}>
        <ModalHeader toggle={() => setSelected(null)}>{selected?.title}</ModalHeader>
        <ModalBody className='feed-search-preview'>
          {selected && <>
            <p className='text-muted'>{[selected.created_by?.first_name, selected.created_by?.last_name].filter(Boolean).join(' ')} · {moment(selected.posted_date).format('DD MMM YYYY')}</p>
            {ReactHtmlParser(selected.description || '')}
            {selected.files?.map((file, index) => <a className='d-block mt-3' key={index} href={getAwsImage(file.url)} target='_blank' rel='noopener noreferrer'>{file.mimetype?.includes('image') ? <img src={getAwsImage(file.url)} alt={file.originalname || 'Bashkëngjitje'} /> : file.originalname || file.name || `Bashkëngjitje ${index + 1}`}</a>)}
          </>}
        </ModalBody>
      </Modal>
    </>
  )
}
